import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { XadomModal } from '@/shared/ui/XadomModal/XadomModal';

import styles from './CompanyManagementModal.module.scss';
import { useCompanyData, useModalState } from './hooks';
import { CompanyDataTransformer } from './model/CompanyDataTransformer';
import { CompanyModalController } from './model/CompanyModalController';

// Helper function to format income type
const getIncomeTypeDisplay = (incomeType: number | null | undefined): string => {
  if (!incomeType) return '-';

  const incomeTypeMap: { [key: number]: string } = {
    30: '30. 부동산임대',
    32: '32. 주택임대',
    40: '40. 사업소득',
    90: '90. 비사업자'
  };

  return incomeTypeMap[incomeType] || `[${incomeType}]`;
};

export const CompanyManagementModal: React.FC = () => {
  const isOpen = useModalState();
  const { companies, companyDetails, modalState } = useCompanyData(isOpen);

  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // 검색 필터링
  const filteredCompanies = useMemo(() => {
    return CompanyDataTransformer.filterCompanies(companies, searchTerm);
  }, [companies, searchTerm]);

  // 기본 선택 회사 설정
  useEffect(() => {
    if (filteredCompanies.length > 0 && !selectedCompanyId) {
      const cId = new URLSearchParams(window.location.search).get('cId');
      const currentCompany = filteredCompanies.find(company => company.companyId === cId);

      if (currentCompany) {
        setSelectedCompanyId(currentCompany.companyId);
      } else {
        setSelectedCompanyId(filteredCompanies[0].companyId);
      }
    }
  }, [filteredCompanies, selectedCompanyId]);

  const selectedCompany = useMemo(() =>
    companies.find(c => c.companyId === selectedCompanyId),
    [companies, selectedCompanyId]
  );

  const selectedDetail = useMemo(() =>
    selectedCompanyId ? companyDetails[selectedCompanyId] : null,
    [companyDetails, selectedCompanyId]
  );

  const handleAccountingClick = useCallback(async () => {
    if (!selectedCompany) return;

    if (selectedCompany.periodStartDate && selectedCompany.periodEndDate && selectedCompany.currentPeriod) {
      const url = CompanyDataTransformer.buildAccountingUrl(
        selectedCompany.companyId,
        `${selectedCompany.currentPeriod}`,
        selectedCompany.periodStartDate,
        selectedCompany.periodEndDate
      );
      window.open(url, '_blank');
    } else if (selectedDetail?.accountingPeriod) {
      const url = CompanyDataTransformer.buildAccountingUrl(
        selectedCompany.companyId,
        selectedDetail.accountingPeriod.period,
        selectedDetail.accountingPeriod.startDate,
        selectedDetail.accountingPeriod.endDate
      );
      window.open(url, '_blank');
    } else {
      console.error('회계기간 정보가 없습니다.');
    }
  }, [selectedCompany, selectedDetail]);

  const handleModalClose = useCallback(() => {
    CompanyModalController.getInstance().hide();
    setSearchTerm('');
    setSelectedCompanyId(null);
  }, []);

  if (modalState.error) {
    return (
      <XadomModal onClose={handleModalClose} isOpen={isOpen}>
        <div className={styles.errorContainer}>
          <div className={styles.errorMessage}>{modalState.error}</div>
          <button onClick={handleModalClose}>닫기</button>
        </div>
      </XadomModal>
    );
  }

  return (
    <XadomModal onClose={handleModalClose} isOpen={isOpen} title="회사 관리">
      <div className={styles.container}>
        {/* 왼쪽 패널 - 회사 목록 */}
        <div className={styles.leftPanel}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="회사명/사업자번호/법인번호/대표자명 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.companyList}>
            {modalState.isLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner} />
                <div>회사 정보를 불러오는 중...</div>
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className={styles.emptyState}>
                <div>검색 결과가 없습니다.</div>
              </div>
            ) : (
              filteredCompanies.map((company) => (
                <div
                  key={company.companyId}
                  className={`${styles.companyItem} ${selectedCompanyId === company.companyId ? styles.selected : ''
                    }`}
                  onClick={() => setSelectedCompanyId(company.companyId)}
                >
                  <div className={styles.companyHeader}>
                    <div className={styles.companyIcon + ' ' + (company.type === 'CORPORATION' ? styles.corporation : styles.individual)}>
                      {company.type === 'CORPORATION' ? '법인' : '개인'}
                    </div>
                    <div className={styles.companyNameContainer}>
                      <div className={styles.companyName}>{company.bizKorNm}</div>
                      <div className={styles.companyInfo}>
                        {`사업자: ${company.bizNo}`}
                        {` | 대표자: ${company.bizCeoKorNm}`}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 오른쪽 패널 - 회사 상세정보 */}
        <div className={styles.rightPanel}>
          {selectedCompany ? (
            <div className={styles.companyDetails}>
              <div className={styles.detailsHeader}>
                <div className={styles.headerContent}>
                  <div className={styles.detailsTitle}>{selectedCompany.bizKorNm}</div>
                </div>
                <button
                  onClick={handleAccountingClick}
                  className={styles.accountingButton}
                  disabled={!selectedCompany || (!selectedCompany.currentPeriod && !selectedDetail?.accountingPeriod)}
                >
                  회계관리 바로가기
                </button>
              </div>

              <div className={styles.detailsContent}>
                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>회사명</div>
                  <div className={styles.detailValue}>
                    {selectedCompany.bizKorNm || '-'}
                  </div>
                </div>

                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>사업자 구분</div>
                  <div className={styles.detailValue}>
                    {CompanyDataTransformer.getCompanyTypeDisplay(selectedCompany.type)}
                  </div>
                </div>

                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>사업자번호</div>
                  <div className={styles.detailValue}>
                    {selectedCompany.bizNo || '-'}
                  </div>
                </div>

                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>법인번호</div>
                  <div className={styles.detailValue}>
                    {selectedCompany.corpNo || '-'}
                  </div>
                </div>

                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>수입구분</div>
                  <div className={styles.detailValue}>
                    {getIncomeTypeDisplay(selectedCompany.incomeType)}
                  </div>
                </div>

                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>대표자</div>
                  <div className={styles.detailValue}>
                    {selectedDetail?.representative?.korName || selectedCompany.bizCeoKorNm || '-'}
                  </div>
                </div>

                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>주소</div>
                  <div className={styles.detailValue}>
                    {CompanyDataTransformer.formatAddress(selectedCompany.addr, selectedCompany.addrDetl) || '-'}
                  </div>
                </div>

                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>우편번호</div>
                  <div className={styles.detailValue}>
                    {selectedCompany.pstCode || '-'}
                  </div>
                </div>

                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>전화번호</div>
                  <div className={styles.detailValue}>
                    {selectedCompany.phone || '-'}
                  </div>
                </div>

                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>팩스번호</div>
                  <div className={styles.detailValue}>
                    {selectedCompany.fax || '-'}
                  </div>
                </div>

                {(selectedCompany.currentPeriod || selectedDetail?.accountingPeriod) && (
                  <div className={styles.detailRow}>
                    <div className={styles.detailLabel}>회계기간</div>
                    <div className={styles.detailValue}>
                      {selectedCompany.currentPeriod && selectedCompany.periodStartDate && selectedCompany.periodEndDate
                        ? `${selectedCompany.currentPeriod}기 (${selectedCompany.periodStartDate} ~ ${selectedCompany.periodEndDate})`
                        : selectedDetail?.accountingPeriod
                          ? `${selectedDetail.accountingPeriod.period} (${selectedDetail.accountingPeriod.startDate} ~ ${selectedDetail.accountingPeriod.endDate})`
                          : '-'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.emptySelection}>
              <div>회사를 선택해주세요.</div>
            </div>
          )}
        </div>
      </div>
    </XadomModal>
  );
};

// Export API for external usage
export const CompanyManagementModalAPI = {
  show: () => CompanyModalController.getInstance().show(),
  hide: () => CompanyModalController.getInstance().hide(),
};